import { NDVIPaletteRGB } from '../../../constants/data';
import { convertSecondsToHoursAndMinutes } from '../../../utils/dates';

// Euclidean distance between RGB
const RgbDist = (r1, g1, b1, r2, g2, b2) => (r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2;

// Linear interpolation of NDVI between nearby colors
const NDVIFromColor = (r, g, b) => {
  let bestIdx = 0;
  let minDist = Infinity;

  // looking for the closest color in the palette
  for (let i = 0; i < NDVIPaletteRGB.length; i++) {
    const [pr, pg, pb] = NDVIPaletteRGB[i].color;
    const d = RgbDist(r, g, b, pr, pg, pb);
    if (d < minDist) {
      minDist = d;
      bestIdx = i;
    }
  }

  const current = NDVIPaletteRGB[bestIdx];
  const prev = NDVIPaletteRGB[Math.max(0, bestIdx - 1)];
  const next = NDVIPaletteRGB[Math.min(NDVIPaletteRGB.length - 1, bestIdx + 1)];

  // linear interpolation between neighbors
  const distPrev = RgbDist(r, g, b, ...prev.color);
  const distNext = RgbDist(r, g, b, ...next.color);

  if (distPrev < distNext) {
    const t = distPrev / (distPrev + minDist);
    return prev.offset * (1 - t) + current.offset * t;
  } else {
    const t = minDist / (minDist + distNext);
    return current.offset * (1 - t) + next.offset * t;
  }
}

// Get average NDVI from image
export async function GetNDVIArrayFromImage(imageUrl) {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = imageUrl;

  await new Promise((res, rej) => {
    img.onload = res;
    img.onerror = rej;
  });

  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);

  const { data } = ctx.getImageData(0, 0, img.width, img.height);
  const ndviArray = new Float32Array(img.width * img.height);
  const colorCache = new Map();

  let sum = 0;
  let count = 0;

  for (let i = 0, j = 0; i < data.length; i += 4, j++) {
    const a = data[i + 3];
    if (a === 0) {
      ndviArray[j] = NaN;
      continue;
    }

    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const key = (r << 16) | (g << 8) | b;

    let ndvi;
    if (colorCache.has(key)) {
      ndvi = colorCache.get(key);
    } else {
      ndvi = NDVIFromColor(r, g, b);
      colorCache.set(key, ndvi);
    }

    ndviArray[j] = ndvi;

    // average
    if (!Number.isNaN(ndvi) && ndvi !== null && ndvi !== undefined) {
      sum += ndvi;
      count++;
    }
  }

  return count > 0 ? parseFloat((sum / count).toFixed(3)) : null;
}

// Marker data
export const GetMarkerValue = ({ param, location }) => {
  let value;
  switch (param) {
    case 'dew':
      value = {
        value: convertSecondsToHoursAndMinutes(location['dewDuration']),
        valueDt: null,
        valueDew: location['dewDuration'],
      };
      break;
    case 'ndvi':
    case 'gdd':
    case 'humidity':
      value = {
        value: location[param].value,
        valueDt: location[param].isIncreasing,
        valueDew: null,
      };
      break;
    case 'bbchAI':
      value = {
        value: location[param] ? location[param].value : '-',
        valueDt: null,
        valueDew: null,
      };
      break;
    case 'temperature':
      value = { value: location[param], valueDt: null, valueDew: null };
      break;
    default:
      value = { value: '-', valueDt: '-', valueDew: null };
  }
  return value;
};

// Location data
export const GetLocationValues = (data) => {
  const gps = data.location.gps_point ? data.location.gps_point : data.location.install_point;
  const lng = gps.coordinates[0];
  const lat = gps.coordinates[1];
  const imgNight = data.photos.night.length ? data.photos.night.at(-1) : '';
  const imgDay = data.photos.day.length ? data.photos.day.at(-1) : '';
  return {
    id: data.location.id,
    name: data.location.name,
    sensorName: data.location.sensor.name,
    dewDuration: data.dewDuration,
    temperature: data.temperature,
    imgNight,
    imgDay,
    bbchAI: data.bbchAI,
    ndvi: data.ndvi,
    gdd: data.gdd,
    humidity: data.humidity,
    isAiEnabled:
      data.location.sensor.settings.enabled &&
      data.location.sensor.settings.enabled.find((item) => item.name === 'ai_photo_analysis'),
    gps_point: gps,
    install_point: data.location.install_point,
    install_date: data.location.install_date,
    removal_date: data.location.removal_date,
    field_season_id: data.location.field_season_id,
    lng,
    lat,
  };
};
